import { apiCall, API, NEW_BE_API, processResponseAsync } from '../../utils/APIFunctions';
import KPConfig from '../../KPConfig.js';
import { abortGlobalRequest } from '../../utils/abortController';

const documentAPI = {
  fetchDocument: (params) => {
    if (!params.id) return {};

    return params.isTemplatePreview ?
      apiCall(`${API}/document_templates/${params.id}`, "get") :
      apiCall(`${NEW_BE_API}/api/n1/documents/${params.id}`, "GET")
    ;
  },

  markReady: (documentId) => {
    return apiCall(`${API}/documents/${documentId}/completed`, "put");
  },

  updateDocumentDate: (documentId, date) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}`, "put",
      JSON.stringify({
        date_ts: date
      })
    );
  },

  moveSection: (documentId, nodeId, isSubSection, fromIndex, toIndex) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/sections/move`, "put",
      JSON.stringify({
        fromIndex: fromIndex,
        toIndex: toIndex,
        isMacroSection: isSubSection
      })
    );
  },

  updateSectionPageBreak: (documentId, nodeId, pageBreak) => {
    return apiCall(
      `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/sections`,
      "put",
      JSON.stringify({pageBreak: pageBreak})
    );
  },

  addRepeatingSection: (documentId, nodeId, macroId, copyOption, templateNodeId) => {
    return apiCall(`${API}/documents/${documentId}/nodes/${nodeId}/macros`, "post",
      JSON.stringify({
        macroId: macroId,
        copyOption: copyOption,
        templateNodeId: templateNodeId
      })
    );
  },

  newAddRepeatingSection: (documentId, nodeId, macroId, copyOption, templateNodeId) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/macros`, "post",
      JSON.stringify({
        macroId: macroId,
        copyOption: copyOption,
        templateNodeId: templateNodeId
      })
    );
  },

  deleteSection: (documentId, nodeId) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/sections`, "delete");
  },

  updateSection: (documentId, nodeId, data) => {
    return apiCall(
      `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/sections`,
      "put",
      JSON.stringify(data)
    );
  },

  uploadImage: (documentId, nodeId, file, index, description, geolocation, aiUsed = false, fullSize = false) => {
    let data = new FormData();
    data.append("file", file);
    data.append("nodeIndex", index);
    data.append("title", description); // description is more descriptive name than title :)
    data.append("aiUsed", aiUsed);
    data.append("fullSize", fullSize);

    if (geolocation) {
      data.append("location_lat", geolocation[0]);
      data.append("location_long", geolocation[1]);
    }

    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/images`, "post", data);
  },

  askAIImageTitle: (documentId, nodeId, file, instructions) => {
    let data = new FormData();
    if (file) {
      data.append("file", file);
    }
    data.append("instructions", instructions);

    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/ask-ai-image-title`, "post", data);
  },
  
  moveImage: (documentId, parentNodeId, fromIndex, toIndex, fromId, toId) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${parentNodeId}/images/reorder`, "put",
      JSON.stringify({
        fromIndex,
        toIndex,
        fromId,
        toId
      })
    );
  },

  moveImageToSection: (documentId, newParentNodeId, originalImageId, originalNode) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${newParentNodeId}/images/move`, "put",
      JSON.stringify({
        originalImageId,
        originalNode
      })
    );
  },

  uploadFile: (documentId, nodeId, file, index, fileName, description, isPublic, aiUsed = false) => {
    let data = new FormData();
    data.append("file", file);
    data.append("name", fileName);
    data.append("nodeIndex", index);
    data.append("title", description); // description is more descriptive name than title :)
    data.append("public", isPublic);
    data.append("aiUsed", aiUsed);
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/files`, "post", data);
  },

  updateFile: (documentId, nodeId, title, isPublic) => {
  
    const payload = {
      ...(title !== undefined && { title }),
      ...(isPublic !== undefined && { public: isPublic }),
    };
  
    return apiCall(
      `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/files`,
      "put",
      JSON.stringify(payload)
    );
  },

  deleteFile: (documentId, nodeId) => {
    return apiCall(
      `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/files`,
      "delete"
    );
  },

  updateCheckbox: (documentId, nodeId, isChecked) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/checkboxes`, "put",
      JSON.stringify({
        isChecked: isChecked
      })
    );
  },
  updateSelectionListNode: (documentId, nodeId, selected) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/selects`, "put",
      JSON.stringify({
        selected: selected
      })
    );
  },
  updateDateTime: (documentId, nodeId, datetime) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/datetimes`, "put",
      JSON.stringify({
        datetime: datetime
      })
    );
  },
  saveSignature: (documentId, nodeId, signatureData) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/signatures`, "put",
      JSON.stringify({
        signature: signatureData
      })
    );
  },
  setSigner: (documentId, nodeId, userId) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/signer`, "put",
      JSON.stringify({
        signingRightsId: userId
      })
    );
  },
  updateImage(params) {
    const data = {
      title: params.title
    };
    if (params.fullSize !== undefined) {
      data.fullSize = params.fullSize;
    }
    if (params.aiUsed !== undefined) {
      data.aiUsed = params.aiUsed;
    }

    return apiCall(`${NEW_BE_API}/api/n1/documents/${params.documentId}/nodes/${params.nodeId}/images`,"put", JSON.stringify(data));
  },

  rotateImage(params) {
    const data = { 
      filename: params.imageNode?.filename,
      originalFilename: params.imageNode?.originalFilename,
      rotation: params.imageNode.rotation || 0,
      angle: params.angle
    };
  
    return apiCall(`${NEW_BE_API}/api/n1/documents/${params.documentId}/nodes/${params.imageNode.id}/images/rotate`, "put", JSON.stringify(data));
  },

  deleteImage(params) {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${params.documentId}/nodes/${params.nodeId}/images`,"delete");
  },

  updateCoverImage(params) {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${params.documentId}`,"put", JSON.stringify(params));
  },

  // Text field related
  askAISuggestion(params) {
    let url = `${NEW_BE_API}/api/n1/documents/${params.documentId}/nodes/${params.nodeId}/ask-ai-suggestion?text=${params.text || ''}`;
    if (params.suggestionType) {
      url += `&suggestionType=${params.suggestionType}`;
    }
    if (params.additionalInstructions) {
      url += `&additionalInstructions=${params.additionalInstructions}`;
    }
    if (params.customInstructions) {
      url += `&customInstructions=${params.customInstructions}`;
    }
    return apiCall(url, "get");
  },

  updateText: (documentId, nodeId, nodeType, text, format, aiUsed) => {
    let type;
    let url;
    if (nodeType === 'input-textfield') {
      type = "textfields";
    } else if (nodeType === 'input-textarea') {
      type = "textareas";
    } else if (nodeType === 'note') {
      type = "notes";
    }
    if (!type) return; // Go away, nothing to see here

    url = `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/${type}`;

    let data = {text: text};
    if (format === 'markdown') {
      data.format = format;
    }
    if (aiUsed) {
      data.aiUsed = aiUsed;
    }

    return apiCall(url, "put", JSON.stringify(data));
  },
  addNote: (documentId, nodeId, text, format) => {
    const url = `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/notes`;

    let data = {text: text};
    if (format === 'markdown') {
      data.format = format;
    }

    return apiCall(url, "post", JSON.stringify(data));
  },
  deleteNote: (documentId, nodeId) => {
    const url = `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/notes`;

    return apiCall(url, "delete", {});
  },

  addTableRow: (documentId, nodeId, values, styles) => {
    const tableRowData = {values: values, styles: styles};

    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/rows`,"post", JSON.stringify(tableRowData));
  },
  updateTableRow: (params) => {
    const {documentId, nodeId, values, styles} = params;
    const tableRowData = { ...(values && { values }), ...(styles && { styles }) };

    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/rows`,"put", JSON.stringify(tableRowData));
  },
  deleteTableRow: (documentId, nodeId) => {
    const url = `${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/rows`;

    return apiCall(url, "delete", {});
  },
  fetchNodeHistory: (documentId, nodeId) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/node-history`, "GET");
  },

  revertNodeChange: (documentId, nodeId, historyId) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/revert-node`,"put",
      JSON.stringify({
        historyId: historyId
      })
    );
  },

  getDocumentStatusId: async (documentId, type, formatParam, globalAbortController) => {
    const format = formatParam ? formatParam : null;

    const params = new URLSearchParams({
      type,
      ...formatParam && { format },
    });

    const timeout = KPConfig.pdfDownloadTimeoutMs;
    const timeoutInSeconds = timeout / 1000;
    const timeoutErrorText = `API call timed out after ${timeoutInSeconds} seconds`;

    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        abortGlobalRequest();
        reject(new Error(timeoutErrorText));
      }, timeout);
    });

    try {
      let url;
      const hasMultipleIds = documentId.toString().includes(',');

      // Use old backend for aiddocs type (only supports single document)
      if (type === 'aiddocs') {
        if (hasMultipleIds) {
          throw new Error('aiddocs type does not support multiple document IDs');
        }
        url = `${KPConfig.backendUrl}/pdf/documents/${documentId}.pdf?${params}`;
      }
      // Use new backend endpoint for multiple documents
      else if (hasMultipleIds) {
        params.append('documentIds', documentId);
        url = `${NEW_BE_API}/api/n1/documents/pdf?${params}`;
      }
      // Use new backend endpoint for single document
      else {
        url = `${NEW_BE_API}/api/n1/documents/${documentId}/pdf?${params}`;
      }

      const apiCallPromise = apiCall(
        url,
        "get",
        null,
        globalAbortController ? globalAbortController.signal : undefined
      );

      const result = await Promise.race([apiCallPromise, timeoutPromise]);
      clearTimeout(timeoutId);
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.message === "Request aborted") {
        console.error(timeoutErrorText);
      } else {
        console.error(error.message);
      }
      throw error;
    }
  }
  ,

  // This is common for all pdfs. So this is also used by floorPlanSlice.
  getPdfProcessedDownloadId: async (statusId, globalAbortController) => {
    const url = `${KPConfig.backendUrl}/pdf/status/${statusId}`;
    let pdfPollInterval = 3000;

  
    const poll = async (resolve, reject) => {
      const timeoutId = setTimeout(() => abortGlobalRequest, KPConfig.pdfDownloadTimeoutMs);
  
      try {
        const res = await fetch(url, {
          credentials: 'include',
          signal: globalAbortController ? globalAbortController.signal : null,
        });
  
        clearTimeout(timeoutId);
  
        const processedResponse = await processResponseAsync(res);
  
        if (!processedResponse.body?.status) {
          resolve(false);
        } else if (processedResponse.body.status === 'failed') {
          resolve(false);
        } else if (processedResponse.body.status === 'completed') {
          resolve(processedResponse.body.download_id);
        } else {
          setTimeout(poll, pdfPollInterval, resolve, reject);
          // Increase poll interval
          if (pdfPollInterval < 30000) {
            pdfPollInterval += 3000;
          }
        }
      } catch (error) {
        clearTimeout(timeoutId);

        if (error.name === 'AbortError') {
          console.log('Fetch aborted:', error.message);
          resolve("AbortError");
        } else {
          resolve(false);
        }
      }
    };
  
    return new Promise(poll);
  }
  ,
  // This is used by downloadJournalWeekPdf
  downloadDocument: (url, fileName) => {
    return fetch(url, {
      credentials: 'include'
    })
      .then((response) => response.blob())
      .then(blob => {

        var blobURL = (window.URL && window.URL.createObjectURL) ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob);
        var tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        tempLink.href = blobURL;
        tempLink.setAttribute('download', fileName);

        // Safari thinks _blank anchor are pop ups. We only want to set _blank
        // target if the browser does not support the HTML5 download attribute.
        // This allows you to download files in desktop safari if pop up blocking
        // is enabled.
        if (typeof tempLink.download === 'undefined') {
          tempLink.setAttribute('target', '_blank');
        }

        document.body.appendChild(tempLink);
        tempLink.click();

        // Fixes "webkit blob resource error 1"
        setTimeout(function () {
          document.body.removeChild(tempLink);
          window.URL.revokeObjectURL(blobURL);
        }, 200);
      });
  },
  lockDocument: (documentId) => {
    return apiCall(`${API}/documents/${documentId}/lock`, "PUT");
  },
  unlockDocument: (documentId) => {
    return apiCall(`${API}/documents/${documentId}/unlock`, "PUT");
  },
  savePrintSettings: (documentId, printSettings) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/print_settings`,"put", JSON.stringify(printSettings));
  },
  follow: (objectId) => {
    return apiCall(`${API}/kp_objects/${objectId}/follow`, "POST");
  },
  unfollow: (objectId) => {
    return apiCall(`${API}/kp_objects/${objectId}/follow`, "DELETE");
  },
  fetchFollowers: (objectId) => {
    return apiCall(`${API}/kp_objects/${objectId}/followers`, "GET");
  },
  sendNotification: (documentId, recipients, message, includePdf) => {
    const data = {recipients: recipients, message: message, includePdf: includePdf};

    return apiCall(`${API}/documents/${documentId}/notifications`,"post", JSON.stringify(data));
  },
  sendDocumentPdfLink(documentId, recipientId) {
    return apiCall(`${API}/documents/${documentId}/sharePdfLink`, "post", JSON.stringify({recipientId}));
  },
  askAIAboutDocument: (documentId, prompt, discussion) => {
    const data = {prompt, discussion};
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/ask-ai`, "post", JSON.stringify(data));
  },
  reorderTableRows: (documentId, nodeId, fromIndex, toIndex) => {
    return apiCall(`${NEW_BE_API}/api/n1/documents/${documentId}/nodes/${nodeId}/rows/reorder`, "put",
      JSON.stringify({
        fromIndex,
        toIndex
      })
    );
  },
};

export default documentAPI;
